
/**
 * Account related functions for Meta API
 */
import { MetaAccount } from './types';
import { API_VERSION } from './constants';
import { 
  enforceRequestInterval, 
  handleRateLimitError,
  isInRateLimitCooldown,
  getRemainingCooldownTime,
  setGlobalRateLimit,
  formatTimeRemaining,
  trackSuccessfulRequest
} from '@/utils/metaApiRateLimiting';

// Endpoint key for tracking request frequency
const ACCOUNTS_ENDPOINT_KEY = 'meta_accounts';

/**
 * Get available Meta ad accounts using direct API call
 */
export async function getAccounts(accessToken: string): Promise<MetaAccount[]> {
  try {
    // First check if we're in a rate limit cooldown period
    if (isInRateLimitCooldown(ACCOUNTS_ENDPOINT_KEY)) {
      const remainingSeconds = getRemainingCooldownTime(ACCOUNTS_ENDPOINT_KEY);
      const formattedTime = formatTimeRemaining(remainingSeconds);
      throw new Error(`Meta API Ratenbegrenzung erreicht. Bitte warten Sie ${formattedTime}.`);
    }
    
    // Enforce request interval and check for rate limiting with specific endpoint key
    await enforceRequestInterval(ACCOUNTS_ENDPOINT_KEY);
    
    console.log('Getting Meta accounts directly');
    
    // First check token permissions
    const permissionCheckUrl = `https://graph.facebook.com/${API_VERSION}/me/permissions?access_token=${accessToken}`;
    const permissionResponse = await fetch(permissionCheckUrl);
    
    if (!permissionResponse.ok) {
      const errorText = await permissionResponse.text();
      console.error('Permission check failed:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error) {
          // Check if this is a rate limit error
          if (errorData.error.code === 4 || errorData.error.message.includes('limit reached')) {
            // Set a global rate limit for all components - 5 minutes
            const rateLimitDuration = 5 * 60; // 5 minutes in seconds
            setGlobalRateLimit(rateLimitDuration);
            const remainingSeconds = getRemainingCooldownTime(ACCOUNTS_ENDPOINT_KEY);
            const formattedTime = formatTimeRemaining(remainingSeconds);
            throw new Error(`Meta API Ratenbegrenzung erreicht. Bitte warten Sie ${formattedTime}.`);
          }
          
          throw new Error(`Meta API Error (${errorData.error.code}): ${errorData.error.message}`);
        }
      } catch (parseError) {
        throw new Error(`Permissions check failed: ${errorText}`);
      }
    }
    
    // Now get ad accounts
    const accountsUrl = `https://graph.facebook.com/${API_VERSION}/me/adaccounts?fields=name,account_id,account_status&access_token=${accessToken}`;
    const accountsResponse = await fetch(accountsUrl);
    
    if (!accountsResponse.ok) {
      const errorText = await accountsResponse.text();
      console.error('Accounts fetch failed:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error) {
          // Check if this is a rate limit error
          if (errorData.error.code === 4 || errorData.error.message.includes('limit reached')) {
            // Set a global rate limit for all components - 5 minutes
            const rateLimitDuration = 5 * 60; // 5 minutes in seconds
            setGlobalRateLimit(rateLimitDuration);
            const remainingSeconds = getRemainingCooldownTime(ACCOUNTS_ENDPOINT_KEY);
            const formattedTime = formatTimeRemaining(remainingSeconds);
            throw new Error(`Meta API Ratenbegrenzung erreicht. Bitte warten Sie ${formattedTime}.`);
          }
          
          throw new Error(`Meta API Error (${errorData.error.code}): ${errorData.error.message}`);
        }
      } catch (parseError) {
        throw new Error(`Accounts fetch failed: ${errorText}`);
      }
    }
    
    const accountsData = await accountsResponse.json();
    
    if (!accountsData.data) {
      throw new Error('No accounts data found in response');
    }
    
    // Track successful request for this endpoint
    trackSuccessfulRequest(ACCOUNTS_ENDPOINT_KEY);
    
    return accountsData.data.map((account: any) => ({
      id: account.id,
      name: account.name,
      status: account.account_status,
    }));
  } catch (error: any) {
    console.error('Error getting accounts:', error);
    
    // Additional check for rate limit errors
    if (error.message && (error.message.includes('limit reached') || error.message.includes('(#4)'))) {
      // Set a global rate limit for all components - 5 minutes
      const rateLimitDuration = 5 * 60; // 5 minutes in seconds
      setGlobalRateLimit(rateLimitDuration);
    }
    
    // Additional check for rate limit errors
    if (error instanceof Error) {
      handleRateLimitError(error);
    }
    
    throw error;
  }
}
